/* eslint-disable import/no-cycle */
import {useState} from 'react';
import * as moment from 'moment';
import {get, map} from 'lodash';

import {observer} from 'mobx-react-lite';
import {useTranslation} from 'react-i18next';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faChartLine} from '@fortawesome/free-solid-svg-icons';
import OperatorWidget from '../operator/shared/OperatorWidget';
import {useStore} from '../../hooks/useStore';
import {useMount} from '../../hooks/useMount';
import {ScopeContext} from '../../policies/scopeContext';
import {processdataCollection} from '../../models/scope';
import {OrderProgressWidgetConfig} from './orderProgressWidgetConfig';
import {WidgetLayouts} from '../shared/widgets/WidgetLayouts';
import {WidgetLayout} from '../../models/widgetLayout';
import {SensorTypes} from '../../models/sensor';
import OrderProgressGraph from './OrderProgressGraph';
/**
 *
 * @param {Array<[number, number]>} actualData
 * @param {Operation} operation
 * @param {number} factor
 * @returns Return series data of prediction line
 */
const predictRemainingData = (actualData = [], operation = {}, factor = 0) => {
  const actualSpeed = operation?.actualSpeed;
  if (!actualData.length || !actualSpeed) {
    return [];
  }
  const predictionData = [];
  const actualRemaining = operation?.order?.plannedQuantity - get(actualData, [actualData.length - 1, 1]);
  if (actualRemaining > 0) {
    const lastActualEntry = get(actualData, [actualData.length - 1]);
    predictionData.push(lastActualEntry);
    predictionData.push([
      (actualRemaining * factor) / actualSpeed + lastActualEntry[0],
      operation?.order?.plannedQuantity,
    ]);
  }

  return predictionData;
};

const OrderProgressWidget = ({disabled, minimized = false, identifier}) => {
  const store = useStore();
  const {t} = useTranslation();
  const [seriesOptions, setSeriesOptions] = useState({actualData: [], predictionData: []});
  const [workplaceSensors, setWorkplaceSensors] = useState([]);

  const widgetConfig = new OrderProgressWidgetConfig(store, identifier);
  const settings = widgetConfig.getValues();
  const scopes = [processdataCollection];

  const workplace = store.workplaceStore.selectedWorkplace;
  const fromDate = moment().subtract(1, 'month');
  const toDate = moment().add(1, 'day');

  const activeOperation = store.operationStore.getActiveByWorkplaceId(workplace?.id);

  const plannedData = store.operationStateLogStore.getPlannedDataByWorkplaceId(
    activeOperation?.id,
    activeOperation?.orderId,
    settings.speed,
    settings.planned
  );

  const loadWorkplaceSensors = async (hierarchyId) => {
    await store.sensorStore.loadAll({
      params: {
        type: [SensorTypes.GoodsReceipt],
      },
    });
    const data = map(store.sensorStore.getByHierarchyIdAndSensorType(hierarchyId, SensorTypes.GoodsReceipt), 'name');
    if (settings.sensor) data.push(settings.sensor);
    setWorkplaceSensors(data);
  };

  const loadWorkplaceOperationLog = (workplaceId) => {
    store.operationStateLogStore.loadManyByWorkplace(workplaceId, {fromDate, toDate});
  };

  const loadSensorDataBySensor = async () => {
    const actualData = await store.sensorDataStore.getActualDataBySensorAndWorkplaceId(
      {fromDate: fromDate.toISOString(), toDate: toDate.toISOString()},
      workplaceSensors,
      activeOperation?.orderId,
      settings.actual
    );
    const predictionData = predictRemainingData(actualData, activeOperation, settings.speed);
    setSeriesOptions({actualData, predictionData});
  };

  useMount(() => {
    if (!workplace) {
      return;
    }
    loadWorkplaceSensors(workplace?.hierarchyId);
  }, [settings.sensor, workplace]);

  useMount(() => {
    if (!workplace) {
      return;
    }
    loadWorkplaceOperationLog(workplace?.id);
  }, [workplace]);

  useMount(() => {
    if (!workplaceSensors.length || !activeOperation?.orderId) {
      setSeriesOptions({actualData: [], predictionData: []});
      return;
    }
    loadSensorDataBySensor();
  }, [workplaceSensors, activeOperation?.orderId]);

  return (
    <ScopeContext.Provider value={scopes}>
      <OperatorWidget
        icon={<FontAwesomeIcon icon={faChartLine}/>}
        title={t('orderProgressWidget.title')}
        disabled={disabled}
        minimized={minimized}
        identifier={identifier}
        isPinable
        widgetConfig={widgetConfig}
      >
        <OrderProgressGraph {...seriesOptions} plannedData={plannedData}/>
      </OperatorWidget>
    </ScopeContext.Provider>
  );
};

OrderProgressWidget.icon = faChartLine;
OrderProgressWidget.isPinable = true;

OrderProgressWidget.identifier = 'OrderProgressWidget';
OrderProgressWidget.defaultLayout = new WidgetLayout({
  identifier: OrderProgressWidget.identifier,
  x: 2,
  y: 8,
  height: 3,
  minHeight: 3,
  width: WidgetLayouts.sixthWidth.w,
  minWidth: WidgetLayouts.sixthWidth.minW,
});

export default observer(OrderProgressWidget);
